import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "../../utils/useTranslation";
import {
  Dimensions,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { useIsFocused } from "@react-navigation/native";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "../../components/shared/Spinner";
import { mdorderHeaders, orderHeaders, smorderHeaders } from "./orderHeaders";
import DeviceOrientationContext from "../../components/context/DeviceOrientationContext";
import { FAB } from "react-native-paper";

import { getOrders } from "../../redux/dispatcher/portalDispatcher";
import {
  setOrderCategoryFilter,
  setOrderPage,
  setOrderSearchQuery,
  setRequestType,
  setOrderDateFilter,
} from "../../redux/reducers/portalReducer";
import { PortalNestedDataTable } from "../../components/custom/DataTable/PortalNestedDataTable";
import { DynamicLabelBannerWithSearchBar } from "../../components/custom/Banners/DynamicLabelBannerWithSearchBar";
import CustomerOrderShipmentStatusUpdate from "./partials/CustomerOrderShipmentStatusUpdate";
import { DatePickerModal, registerTranslation } from "react-native-paper-dates";

registerTranslation("en-AU", {
  save: "Save",
  selectSingle: "Select date",
  selectMultiple: "Select dates",
  selectRange: "Select period",
  notAccordingToDateFormat: (inputFormat) =>
    `Date format must be ${inputFormat}`,
  mustBeHigherThan: (date) => `Must be later than ${date}`,
  mustBeLowerThan: (date) => `Must be earlier than ${date}`,
  mustBeBetween: (startDate, endDate) =>
    `Must be between ${startDate} - ${endDate}`,
  dateIsDisabled: "Day is not allowed",
  previous: "Previous",
  next: "Next",
  typeInDate: "Type in date",
  pickDateFromCalendar: "Pick date from calendar",
  close: "Close",
  hour: "Hour",
  minute: "Minute",
  startDate: "Start Date (DD/MM/YYYY)",
  endDate: "End Date (DD/MM/YYYY)",
});

export default function CustomerOrderScreen({ navigation }: any) {
  const isFocused = useIsFocused();
  const dispatch = useDispatch<any>();
  const { t } = useTranslation();

  // Get portal state from Redux
  const {
    orders,
    loading,
    searching,
    totalCount,
    totalPages,
    page,
    pageSize,
    searchQuery,
    categoryFilter,
    sortBy,
    requestType,
    dateFilter,
  } = useSelector((state: any) => state.portal);

  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      orderHeaders,
    );

  // Customer Modal Props
  const [modalOpen, setModalOpen] = useState<boolean>();
  const [selectedEnquiryType, setSelectedEnquiryType] = useState<any>(null);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const onClose = () => {
    setModalOpen(false);
  };

  const { isLargeDevice, isMediumDevice, isMobileDevice, isSmallDevice } =
    useContext(DeviceOrientationContext);

  const dateFiltersList = [
    "All",
    "Last 7 days",
    "Yesterday",
    "Today",
    "Bi-Weekly",
    "Next 7 days",
    "Next 14 days",
    "Next 30 days",
    "Date Range",
  ];
  const requestTypeList = ["Request Delivery", "Made To Order", "Pick up"];
  const statusList = [
    "Cancelled",
    "Pending For Approval",
    "Pending For Paperwork",
    "Approved",
    "Ready",
    "Back-Order",
    "Shipped",
  ];

  const listRef = useRef<ScrollView>(null);
  const [contentOffSet, setContentOffSet] = useState<number>(0);
  const [fabPosition] = useState<number>(320);
  const [isAtBottom, setIsAtBottom] = useState<boolean>(false);

  const handleScroll = (event: any) => {
    const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
    setContentOffSet(contentOffset.y);
    
    const isCloseToBottom = layoutMeasurement.height + contentOffset.y >= contentSize.height - 200;
    setIsAtBottom(isCloseToBottom);
  };

  const navigateToOrder = (order: any, action: string) => {
    if (action === "view") {
      navigation.navigate("CustomerOrderSummaryReport", { order });
    } else if (action === "update_status") {
      setModalOpen(true);
      setSelectedItem(order);
      setSelectedEnquiryType(action);
    }
  };

  // Fetch orders on mount and when filters change
  useEffect(() => {
    if (isFocused) {
      dispatch(getOrders() as any).unwrap();
    }
  }, [
    isFocused,
    dispatch,
    page,
    searchQuery,
    categoryFilter,
    sortBy,
    requestType,
    dateFilter,
  ]);

  // Update table headers based on device size
  useEffect(() => {
    setTableHeaders(
      isLargeDevice
        ? orderHeaders
        : isMediumDevice
          ? mdorderHeaders
          : smorderHeaders,
    );
  }, [isLargeDevice, isMediumDevice, isSmallDevice]);

  const screenWidth = Dimensions.get("window").width;

  const shouldUseCompactControls =
    isSmallDevice || isMobileDevice || screenWidth <= 1366;

  // Handle search
  const handleSearch = (query: string) => {
    dispatch(setOrderSearchQuery(query));
    dispatch(setOrderPage(0)); // reset page on search
  };

  const handleSearchTextChange = (text: string) => {
    dispatch(setOrderSearchQuery(text));
  };

  const handleQuickFilterChange = (selected: string[]) => {
    const requestTypes = ["Request Delivery", "Made To Order", "Pick up"];
    const dateFilters = ["Last 7 days", "Yesterday", "Today", "Bi-Weekly", "Next 7 days", "Next 14 days", "Next 30 days"];
    const selectedRequestTypes = selected.filter((s) =>
      requestTypes.includes(s),
    );
    const selectedDateFilters = selected.filter((s) => dateFilters.includes(s));
    const selectedStatuses = selected.filter(
      (s) => !requestTypes.includes(s) && !dateFilters.includes(s),
    );

    dispatch(
      setRequestType(
        selectedRequestTypes.length === 0
          ? "all"
          : selectedRequestTypes.join(","),
      ),
    );
    dispatch(
      setOrderCategoryFilter(
        selectedStatuses.length === 0 ? "all" : selectedStatuses.join(","),
      ),
    );
    dispatch(
      setOrderDateFilter(
        selectedDateFilters.length === 0
          ? "all"
          : selectedDateFilters.join(","),
      ),
    );
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    dispatch(setOrderPage(newPage));
  };

  // Handle refresh
  const handleRefresh = () => {
    dispatch(getOrders() as any);
  };

  const [selectedDates, setSelectedDates] = useState<string[]>([
    dateFilter || "Bi-Weekly",
  ]);
  const [selectedRequestTypes, setSelectedRequestTypes] = useState<string[]>(
    requestType === "all" || !requestType ? [] : requestType.split(","),
  );
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(
    categoryFilter === "all" || !categoryFilter
      ? []
      : categoryFilter.split(","),
  );

  const [mobileFilterOpen, setMobileFilterOpen] = useState<
    "date" | "requestType" | "status" | null
  >(null);

  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [customDateRange, setCustomDateRange] = useState<{
    startDate: Date | undefined;
    endDate: Date | undefined;
  }>({ startDate: undefined, endDate: undefined });

  const formatLocalDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDateConfirm = (params: any) => {
    setDatePickerOpen(false);
    if (params.startDate) {
      const start = params.startDate;
      const end = params.endDate || params.startDate;
      setCustomDateRange({ startDate: start, endDate: end });
      const startDateStr = formatLocalDate(start);
      const endDateStr = formatLocalDate(end);
      dispatch(setOrderDateFilter(`Custom:${startDateStr}_${endDateStr}`));
      setSelectedDates([`Custom:${startDateStr}_${endDateStr}`]);
    }
  };

  const getDisplayDate = (dateStr: string) => {
    if (!dateStr) return "All";
    if (dateStr.startsWith("Custom:")) {
      const dates = dateStr.replace("Custom:", "").split("_");
      if (dates.length === 2) {
        const [startYear, startMonth, startDay] = dates[0].split("-");
        const [endYear, endMonth, endDay] = dates[1].split("-");
        return `${startDay}/${startMonth}/${startYear} to ${endDay}/${endMonth}/${endYear}`;
      }
    }
    return dateStr;
  };

  const handleDateDismiss = () => {
    setDatePickerOpen(false);
    if (selectedDates.length === 0 || selectedDates[0].startsWith("Custom")) {
      // Do nothing if already custom or empty
    }
  };

  const columnsToNotHaveThousandSeparator: Array<string> = [
    "packingNo",
    "Hubspot ID",
    "docNumbers",
    "Customer Number",
    "orderNo",
  ];

  return (
    <View style={styles.container}>
      {isSmallDevice || isMobileDevice ? (
        <>
          {/* Mobile: Search bar full width on top */}
          <View style={{ paddingHorizontal: 10, paddingTop: 10 }}>
            <DynamicLabelBannerWithSearchBar
              titleName={`${t("totalItems")}: ${totalCount || 0}`}
              setRefresh={handleRefresh}
              dataToExport={orders}
              fileName="orderReport"
              sheetName="Orders"
              disableTitle={false}
              noBorder
              handleSearch={handleSearch}
              onSearchTextChange={handleSearchTextChange}
              _searchText={searchQuery}
            />
          </View>

          {/* Mobile: Dropdown filter buttons */}
          <View style={styles.mobileFilterWrapper}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <TouchableOpacity
                style={[
                  styles.mobileDropdownButton,
                  mobileFilterOpen === "date" &&
                    styles.mobileDropdownButtonActive,
                ]}
                onPress={() =>
                  setMobileFilterOpen(
                    mobileFilterOpen === "date" ? null : "date",
                  )
                }
              >
                <Text style={styles.mobileDropdownLabel}>{t("date")}: </Text>
                <Text style={styles.mobileDropdownValue}>
                  {selectedDates.length === 0 ? "All" : selectedDates[0]}
                </Text>
                <Text style={styles.mobileDropdownArrow}>
                  {mobileFilterOpen === "date" ? "▲" : "▼"}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.mobileDropdownButton,
                  mobileFilterOpen === "requestType" &&
                    styles.mobileDropdownButtonActive,
                  { marginLeft: 16 },
                ]}
                onPress={() =>
                  setMobileFilterOpen(
                    mobileFilterOpen === "requestType" ? null : "requestType",
                  )
                }
              >
                <Text style={styles.mobileDropdownLabel}>
                  {t("requestType")}:{" "}
                </Text>
                <Text style={styles.mobileDropdownValue}>
                  {selectedRequestTypes.length === 0
                    ? "All"
                    : selectedRequestTypes.length === 1
                      ? selectedRequestTypes[0]
                      : `${selectedRequestTypes.length} ${t("selected")}`}
                </Text>
                <Text style={styles.mobileDropdownArrow}>
                  {mobileFilterOpen === "requestType" ? "▲" : "▼"}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.mobileDropdownButton,
                  mobileFilterOpen === "status" &&
                    styles.mobileDropdownButtonActive,
                  { marginLeft: 16 },
                ]}
                onPress={() =>
                  setMobileFilterOpen(
                    mobileFilterOpen === "status" ? null : "status",
                  )
                }
              >
                <Text style={styles.mobileDropdownLabel}>{t("status")}: </Text>
                <Text style={styles.mobileDropdownValue}>
                  {selectedStatuses.length === 0
                    ? "All"
                    : selectedStatuses.length === 1
                      ? selectedStatuses[0]
                      : `${selectedStatuses.length} ${t("selected")}`}
                </Text>
                <Text style={styles.mobileDropdownArrow}>
                  {mobileFilterOpen === "status" ? "▲" : "▼"}
                </Text>
              </TouchableOpacity>
            </ScrollView>

            {mobileFilterOpen === "date" && (
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={[
                  styles.mobileChipRow,
                  { marginTop: 10 },
                ]}
              >
                {dateFiltersList.map((option, index) => {
                  const isActive =
                    option === "All"
                      ? selectedDates.length === 0
                      : option === "Date Range"
                        ? selectedDates.length > 0 && selectedDates[0].startsWith("Custom")
                        : selectedDates.includes(option);
                  return (
                    <TouchableOpacity
                      key={`date-${index}`}
                      style={[
                        styles.filterChip,
                        isActive && styles.filterChipActive,
                      ]}
                      onPress={() => {
                        if (option === "All") {
                          setSelectedDates([]);
                          dispatch(setOrderDateFilter("all"));
                        } else if (option === "Date Range") {
                          setDatePickerOpen(true);
                        } else {
                          const newDates = selectedDates.includes(option)
                            ? []
                            : [option];
                          setSelectedDates(newDates);
                          dispatch(
                            setOrderDateFilter(
                              newDates.length === 0 ? "all" : newDates[0],
                            ),
                          );
                        }
                      }}
                    >
                      <Text
                        style={
                          isActive
                            ? styles.filterChipTextActive
                            : styles.filterChipText
                        }
                      >
                        {option}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            )}

            {mobileFilterOpen === "requestType" && (
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={[
                  styles.mobileChipRow,
                  { marginTop: 10 },
                ]}
              >
                <TouchableOpacity
                  style={[
                    styles.filterChip,
                    selectedRequestTypes.length === 0 &&
                      styles.filterChipActive,
                  ]}
                  onPress={() => {
                    setSelectedRequestTypes([]);
                    dispatch(setRequestType("all"));
                  }}
                >
                  <Text
                    style={
                      selectedRequestTypes.length === 0
                        ? styles.filterChipTextActive
                        : styles.filterChipText
                    }
                  >
                    All
                  </Text>
                </TouchableOpacity>
                {requestTypeList.map((option, index) => {
                  const isActive = selectedRequestTypes.includes(option);
                  return (
                    <TouchableOpacity
                      key={`rt-${index}`}
                      style={[
                        styles.filterChip,
                        isActive && styles.filterChipActive,
                      ]}
                      onPress={() => {
                        const newReqs = isActive
                          ? selectedRequestTypes.filter((s) => s !== option)
                          : [...selectedRequestTypes, option];
                        setSelectedRequestTypes(newReqs);
                        dispatch(
                          setRequestType(
                            newReqs.length === 0 ? "all" : newReqs.join(","),
                          ),
                        );
                      }}
                    >
                      <Text
                        style={
                          isActive
                            ? styles.filterChipTextActive
                            : styles.filterChipText
                        }
                      >
                        {option}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            )}

            {mobileFilterOpen === "status" && (
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={[
                  styles.mobileChipRow,
                  { marginTop: 10 },
                ]}
              >
                <TouchableOpacity
                  style={[
                    styles.filterChip,
                    selectedStatuses.length === 0 && styles.filterChipActive,
                  ]}
                  onPress={() => {
                    setSelectedStatuses([]);
                    dispatch(setOrderCategoryFilter("all"));
                  }}
                >
                  <Text
                    style={
                      selectedStatuses.length === 0
                        ? styles.filterChipTextActive
                        : styles.filterChipText
                    }
                  >
                    All
                  </Text>
                </TouchableOpacity>
                {statusList.map((option, index) => {
                  const isActive = selectedStatuses.includes(option);
                  return (
                    <TouchableOpacity
                      key={`st-${index}`}
                      style={[
                        styles.filterChip,
                        isActive && styles.filterChipActive,
                      ]}
                      onPress={() => {
                        const newStatus = isActive
                          ? selectedStatuses.filter((s) => s !== option)
                          : [...selectedStatuses, option];
                        setSelectedStatuses(newStatus);
                        dispatch(
                          setOrderCategoryFilter(
                            newStatus.length === 0
                              ? "all"
                              : newStatus.join(","),
                          ),
                        );
                      }}
                    >
                      <Text
                        style={
                          isActive
                            ? styles.filterChipTextActive
                            : styles.filterChipText
                        }
                      >
                        {option}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            )}
          </View>
        </>
      ) : (
        /* Desktop: Filters on left, Search on right */
        <>
          <View style={styles.quickFilterBar}>
            <View style={{ flex: 1 }}>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.quickFilterChipsContainer}
              >
                <Text style={styles.quickFilterLabel}>{t("quickFilter")}</Text>
                <TouchableOpacity
                  style={[
                    styles.mobileDropdownButton,
                    mobileFilterOpen === "date" &&
                      styles.mobileDropdownButtonActive,
                  ]}
                  onPress={() =>
                    setMobileFilterOpen(
                      mobileFilterOpen === "date" ? null : "date",
                    )
                  }
                >
                  <Text style={styles.mobileDropdownLabel}>{t("date")}: </Text>
                  <Text style={styles.mobileDropdownValue}>
                    {selectedDates.length === 0 ? "All" : getDisplayDate(selectedDates[0])}
                  </Text>
                  <Text style={styles.mobileDropdownArrow}>
                    {mobileFilterOpen === "date" ? "▲" : "▼"}
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[
                    styles.mobileDropdownButton,
                    mobileFilterOpen === "requestType" &&
                      styles.mobileDropdownButtonActive,
                    { marginLeft: 8 },
                  ]}
                  onPress={() =>
                    setMobileFilterOpen(
                      mobileFilterOpen === "requestType" ? null : "requestType",
                    )
                  }
                >
                  <Text style={styles.mobileDropdownLabel}>
                    {t("requestType")}:{" "}
                  </Text>
                  <Text style={styles.mobileDropdownValue}>
                    {selectedRequestTypes.length === 0
                      ? "All"
                      : selectedRequestTypes.length === 1
                        ? selectedRequestTypes[0]
                        : `${selectedRequestTypes.length} ${t("selected")}`}
                  </Text>
                  <Text style={styles.mobileDropdownArrow}>
                    {mobileFilterOpen === "requestType" ? "▲" : "▼"}
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[
                    styles.mobileDropdownButton,
                    mobileFilterOpen === "status" &&
                      styles.mobileDropdownButtonActive,
                    { marginLeft: 8 },
                  ]}
                  onPress={() =>
                    setMobileFilterOpen(
                      mobileFilterOpen === "status" ? null : "status",
                    )
                  }
                >
                  <Text style={styles.mobileDropdownLabel}>
                    {t("status")}:{" "}
                  </Text>
                  <Text style={styles.mobileDropdownValue}>
                    {selectedStatuses.length === 0
                      ? "All"
                      : selectedStatuses.length === 1
                        ? selectedStatuses[0]
                        : `${selectedStatuses.length} ${t("selected")}`}
                  </Text>
                  <Text style={styles.mobileDropdownArrow}>
                    {mobileFilterOpen === "status" ? "▲" : "▼"}
                  </Text>
                </TouchableOpacity>
              </ScrollView>
            </View>
            <View style={styles.quickFilterSearchSection}>
              <DynamicLabelBannerWithSearchBar
                titleName={`${t("totalItems")}: ${totalCount || 0}`}
                setRefresh={handleRefresh}
                dataToExport={orders}
                fileName="orderReport"
                sheetName="Orders"
                disableTitle={false}
                noBorder
                handleSearch={handleSearch}
                onSearchTextChange={handleSearchTextChange}
                _searchText={searchQuery}
              />
            </View>
          </View>

          {/* Desktop Expanded Chips */}
          {mobileFilterOpen === "date" && (
            <View style={styles.mobileFilterWrapper}>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.mobileChipRow}
              >
                {dateFiltersList.map((option, index) => {
                  const isActive =
                    option === "All"
                      ? selectedDates.length === 0
                      : option === "Date Range"
                        ? selectedDates.length > 0 && selectedDates[0].startsWith("Custom")
                        : selectedDates.includes(option);
                  return (
                    <TouchableOpacity
                      key={`date-${index}`}
                      style={[
                        styles.filterChip,
                        isActive && styles.filterChipActive,
                      ]}
                      onPress={() => {
                        if (option === "All") {
                          setSelectedDates([]);
                          dispatch(setOrderDateFilter("all"));
                        } else if (option === "Date Range") {
                          setDatePickerOpen(true);
                        } else {
                          const newDates = selectedDates.includes(option)
                            ? []
                            : [option];
                          setSelectedDates(newDates);
                          dispatch(
                            setOrderDateFilter(
                              newDates.length === 0 ? "all" : newDates[0],
                            ),
                          );
                        }
                      }}
                    >
                      <Text
                        style={
                          isActive
                            ? styles.filterChipTextActive
                            : styles.filterChipText
                        }
                      >
                        {option}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            </View>
          )}

          {mobileFilterOpen === "requestType" && (
            <View style={styles.mobileFilterWrapper}>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.mobileChipRow}
              >
                <TouchableOpacity
                  style={[
                    styles.filterChip,
                    selectedRequestTypes.length === 0 &&
                      styles.filterChipActive,
                  ]}
                  onPress={() => {
                    setSelectedRequestTypes([]);
                    dispatch(setRequestType("all"));
                  }}
                >
                  <Text
                    style={
                      selectedRequestTypes.length === 0
                        ? styles.filterChipTextActive
                        : styles.filterChipText
                    }
                  >
                    All
                  </Text>
                </TouchableOpacity>
                {requestTypeList.map((option, index) => {
                  const isActive = selectedRequestTypes.includes(option);
                  return (
                    <TouchableOpacity
                      key={`rt-${index}`}
                      style={[
                        styles.filterChip,
                        isActive && styles.filterChipActive,
                      ]}
                      onPress={() => {
                        const newReqs = isActive
                          ? selectedRequestTypes.filter((s) => s !== option)
                          : [...selectedRequestTypes, option];
                        setSelectedRequestTypes(newReqs);
                        dispatch(
                          setRequestType(
                            newReqs.length === 0 ? "all" : newReqs.join(","),
                          ),
                        );
                      }}
                    >
                      <Text
                        style={
                          isActive
                            ? styles.filterChipTextActive
                            : styles.filterChipText
                        }
                      >
                        {option}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            </View>
          )}

          {mobileFilterOpen === "status" && (
            <View style={styles.mobileFilterWrapper}>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.mobileChipRow}
              >
                <TouchableOpacity
                  style={[
                    styles.filterChip,
                    selectedStatuses.length === 0 && styles.filterChipActive,
                  ]}
                  onPress={() => {
                    setSelectedStatuses([]);
                    dispatch(setOrderCategoryFilter("all"));
                  }}
                >
                  <Text
                    style={
                      selectedStatuses.length === 0
                        ? styles.filterChipTextActive
                        : styles.filterChipText
                    }
                  >
                    All
                  </Text>
                </TouchableOpacity>
                {statusList.map((option, index) => {
                  const isActive = selectedStatuses.includes(option);
                  return (
                    <TouchableOpacity
                      key={`st-${index}`}
                      style={[
                        styles.filterChip,
                        isActive && styles.filterChipActive,
                      ]}
                      onPress={() => {
                        const newStatus = isActive
                          ? selectedStatuses.filter((s) => s !== option)
                          : [...selectedStatuses, option];
                        setSelectedStatuses(newStatus);
                        dispatch(
                          setOrderCategoryFilter(
                            newStatus.length === 0
                              ? "all"
                              : newStatus.join(","),
                          ),
                        );
                      }}
                    >
                      <Text
                        style={
                          isActive
                            ? styles.filterChipTextActive
                            : styles.filterChipText
                        }
                      >
                        {option}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            </View>
          )}
        </>
      )}

      {modalOpen && selectedEnquiryType === "update_status" && (
        <CustomerOrderShipmentStatusUpdate
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      <ScrollView
        style={styles.contentContainer}
        onScroll={handleScroll}
        ref={listRef}
      >
        {loading && !searching ? (
          <View style={styles.spinnerContainer}>
            <Spinner size="small" color="#74BBFF" />
          </View>
        ) : (
          <View style={{ backgroundColor: "#fff" }}>
            {isAtBottom && (
              <FAB
                icon="arrow-up"
                style={[
                  styles.fab,
                  {
                    top:
                      contentOffSet +
                      Dimensions.get("window").height -
                      fabPosition -
                      120,
                  },
                ]}
                isAtBottom={isAtBottom}
                onPress={() => {
                  if (listRef.current) {
                    listRef.current.scrollTo({ y: 0, animated: true });
                  }
                }}
                small
              />
            )}
            <PortalNestedDataTable
              itemsPerPage={pageSize}
              isNestedDataLoaded={!loading}
              nestedHeader={tableHeaders}
              nestedRows={orders}
              rowKeyValue={"documentNumber"}
              key="rowKeyValue"
              nestedColumnsToNotHaveThousandSeparator={
                columnsToNotHaveThousandSeparator
              }
              isDisplayTimeWithDate={false}
              handleNestedSortColumn={() => {}}
              disableHeader={false}
              operationActions={navigateToOrder}
              isDetail={false}
              setModalVisible={() => {}}
              handleRowClick={() => {}}
              setSelected={() => {}}
              // Server-side pagination props
              useServerPagination={true}
              currentPage={page}
              totalPages={totalPages}
              totalCount={totalCount}
              onPageChange={handlePageChange}
              loading={searching}
              disableStatusBadge={true}
              allowDefaultView={false}
            />
          </View>
        )}
      </ScrollView>
      <DatePickerModal
        locale="en-AU"
        mode="range"
        visible={datePickerOpen}
        onDismiss={handleDateDismiss}
        startDate={customDateRange.startDate}
        endDate={customDateRange.endDate}
        onConfirm={handleDateConfirm}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
  },
  quickFilterBar: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#fff",
    paddingTop: 8,
    paddingBottom: 4,
    paddingHorizontal: 10,
  },
  quickFilterChipsContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingRight: 12,
  },
  quickFilterLabel: {
    fontWeight: "700",
    fontSize: 13,
    color: "#374151",
    marginRight: 10,
  },
  quickFilterSearchSection: {
    flexShrink: 0,
    minWidth: 350,
  },
  mobileFilterWrapper: {
    paddingHorizontal: 10,
    paddingVertical: 10,
  },
  mobileDropdownButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: "#F1F5F9",
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    alignSelf: "flex-start",
  },
  mobileDropdownButtonActive: {
    borderColor: "#61B8FF",
    backgroundColor: "#EFF6FF",
  },
  mobileDropdownLabel: {
    fontSize: 12,
    fontWeight: "600",
    color: "#6B7280",
  },
  mobileDropdownValue: {
    fontSize: 12,
    fontWeight: "700",
    color: "#1E40AF",
  },
  mobileDropdownArrow: {
    fontSize: 8,
    color: "#6B7280",
    marginLeft: 4,
  },
  mobileChipRow: {
    flexDirection: "row",
    paddingVertical: 2,
    paddingHorizontal: 4,
  },
  contentContainer: {
    flex: 1,
    backgroundColor: "#f8f9fa",
  },
  spinnerContainer: {
    justifyContent: "center",
    alignItems: "center",
    marginTop: 20,
  },
  fab: {
    position: "absolute",
    margin: 16,
    right: 0,
    zIndex: 1,
  },
  filterChip: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    backgroundColor: "#F1F5F9",
    borderRadius: 999,
    marginRight: 12,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  filterChipActive: {
    backgroundColor: "#61B8FF",
    borderColor: "#61B8FF",
  },
  filterChipText: {
    fontSize: 13,
    color: "#475569",
    fontWeight: "500" as const,
  },
  filterChipTextActive: {
    color: "#fff",
    fontSize: 13,
    fontWeight: "600" as const,
  },
});
